import data from "../../shared/sharedData";

export default {
  name: "Appendix C - Foreign Labor Recruiter Information",
  // prefix: "I",
  pageInfo: {
    text: `Pursuant to 20 CFR 655.9(b), the employer and its attorney or agent (as applicable) must disclose to the Department the identity (name) and geographic location 
    of persons and entities hired by, or working for, the foreign labor recruiter who recruits prospective foreign workers for the H-2B job opportunities offered by the employer 
    under this <i>H-2B Application for Temporary Employment Certification</i>, Form ETA-9142B.  This disclosure includes the names of agents and foreign labor recruiters used by the employer, 
    as well as the identities and locations of all persons or entities hired by or working for the primary recruiter in the recruitment of prospective H-2B workers, and the agents or 
    employees of these entities.  This disclosure is required for all agreements, whether written or verbal, and the required disclosure covers the entirety of the recruitment that 
    brings an H-2B foreign worker to the employer’s certified H-2B job opportunity in the United States.  
    <br></br>For each person or entity, complete a section of the Appendix C form by providing identity and location information.  If the employer has more than five (5) persons and entities 
    to identify, the employer must complete as many additional Appendix C forms as are necessary to disclose all persons or entities engaged in foreign worker recruitment for this application.  
    <br></br><b>Important Note</b>:  Employers are required to complete Appendix C to supply information about foreign labor recruiter(s).  Submission of this information in any other form or format 
    (e.g., a list included in a Foreign Labor Recruitment Agreement) will not be considered as satisfying this disclosure requirement and will result in the OFLC National Processing Center 
    issuing a NOD that requests a completed Appendix C.  Complete the items with the identity and location of each person/recruiter who the employer has engaged or plans to engage, directly or 
    indirectly, to recruit foreign workers for the job opportunities in this application.  Those items marked with an asterisk (*) are required and must be completed.
    `,
    coloring: `grey`,
    // read_more: true,
    // character_limit: 1428,
    isVisible: {
      when: "empflrecEngageH2b",
      is: ["YES"],
      operator: "compareMultiFieldValuesAND_ORInArray",
    },
  },
  modalPrefill: {
    type: "foreign-labor",
    machine_name: "recruiters",
    modal_name: "addProfilesModal",
    title: "Foreign Labor Recruiter Profiles",
    description:
      "Select the Foreign Labor Recruiter profile(s) to populate in the Appendix C table.",
    isVisible: {
      when: "empflrecEngageH2b",
      is: ["YES"],
      operator: "compareMultiFieldValuesAND_ORInArray",
    },
  },
  pageAlert: {
    widget: "pageAlert",
    alerts: [
      {
        title: "Information Required",
        description:
          "Please specify in Field G.5 whether you are engaging or planning to engage any agent(s) or recruiter(s) in the recruitment of prospective H-2B workers.",
        type: "info",
        isVisible: {
          when: "empflrecEngageH2b",
          is: "untouched",
          operator: "untouched",
        },
      },
      {
        title: "Section Complete",
        description:
          "Because you indicated that you are not engaging or planning to engage any agent(s) or recruiter(s) in the recruitment of prospective H-2B workers (per Field G.5), no Appendix C is necessary for this application.",
        type: "success",
        isVisible: {
          when: "empflrecEngageH2b",
          is: "NO",
        },
      },
    ],
  },
  sections: [
    {
      code: "Appendix C",
      machine_name: "recruiters",
      isModal: true,
      removeFromFieldArray: true,
      openModalHeader: "Add Foreign Labor Recruiter(s)",
      modalHeader: "Add Foreign Labor Recruiter",
      sectionHeader: "Foreign Labor Recruiter(s)",
      listType: "table",
      validate: ["tableRequired", "tableIncomplete"],
      editButton: true,
      removeLink: "Remove Foreign Labor Recruiter",
      isVisible: {
        when: "empflrecEngageH2b",
        is: "YES",
      },

      modalFields: [
        {
          machine_name: "apdxcRecLastname",
          label: "Recruiter's Last (family) Name",
          widget: "text",
          validate: ["required"],
          maxLength: 35,
          tooltip:
            "Enter the last (family) name of the person/recruiter. If the person/recruiter has two last names, enter the primary last name first.",
        },
        {
          machine_name: "apdxcRecFirstname",
          label: "First (given) Name",
          widget: "text",
          validate: ["required"],
          maxLength: 35,
          tooltip: "Enter the first (given) name of the person/recruiter.",
        },
        {
          machine_name: "apdxcRecMiddlename",
          label: "Middle Name(s)",
          widget: "text",
          maxLength: 35,
          hideOnTable: true,
          tooltip:
            "Enter the middle name(s) of the person/recruiter, if applicable. Enter “N/A” if not applicable",
        },
        {
          machine_name: "apdxcRecOrg",
          label: "Name of Employer/Recruiting Organization",
          widget: "text",
          validate: ["required"],
          maxLength: 35,
          hideOnTable: true,
          tooltip: `Enter the name of the company or recruiting organization that the person/recruiter operates or for which the 
          person/recruiter works. If the person/recruiter recruits directly for the employer and does not operate through a company or 
          recruiting organization, enter “N/A.” If the person/recruiter recruits indirectly for the employer (i.e., through another 
          person or entity), enter the full name of the person or entity for which the person/recruiter directly provides services.`,
        },
        {
          machine_name: "apdxcRecCountry",
          label: "Country",
          widget: "select",
          selection: data.country,
          clearSchema: ["apdxcRecState", "apdxcRecProvince"],
          prefix: "add_values_field",
          validate: ["required"],
          hideOnTable: true,
          tooltip:
            "Enter the country in which the person/recruiter is located.",
        },
        {
          machine_name: "apdxcRecCity",
          label: "City",
          widget: "text",
          validate: ["required"],
          hideOnTable: true,
          tooltip: "Enter the city in which the person/recruiter is located.",
        },
        {
          machine_name: "apdxcRecState",
          label: "State",
          widget: "select",
          selection: data.state,
          state: true,
          maxLength: 30,
          isVisible: {
            when: "apdxcRecCountry",
            is: "UNITED STATES OF AMERICA",
            prefix: "add_values_field",
          },
          validate: [
            "required",
            {
              isRequired: "apdxcRecCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
                prefix: "add_values_field",
                modal: true,
              },
            },
          ],
          hideOnTable: true,
          tooltip:
            "Enter the State, District, or Territory in which the person/recruiter is located. If the geographic location does not have a State, District, or Territory designation, enter “N/A.”",
        },
        {
          machine_name: "apdxcRecState",
          label: "State",
          widget: "text",
          maxLength: 30,
          isVisible: {
            when: "apdxcRecCountry",
            is: "UNITED STATES OF AMERICA",
            operator: "!==",
            prefix: "add_values_field",
          },
          validate: ["required"],
          hideOnTable: true,
          tooltip: `Enter the State, District, or Territory in which the person/recruiter is located. If the geographic location does not have a State, District, or Territory designation, enter “N/A.”`,
        },

        {
          machine_name: "apdxcRecPostcode",
          label: "Postal code",
          widget: "text",
          validate: [
            {
              postalCountry: "apdxcRecCountry",
              needsFieldValues: true,
              extraData: {
                valueOf: "apdxcRecCountry",
                prefix: "add_values_field",
                modal: true,
                allowNAs: true,
              },
            },
            {
              isRequired: "apdxcRecCountry",
              message: "This field is required",
              needsFieldValues: true,
              extraData: {
                valueOf: "UNITED STATES OF AMERICA",
                prefix: "add_values_field",
                modal: true,
              },
            },
          ],
          maxLength: 10,
          hideOnTable: true,
          tooltip:
            "Enter the postal (zip) code in which the person/recruiter is located. If the geographic location does not have a postal code designation, enter “N/A.”",
        },
        {
          machine_name: "apdxcRecProvince",
          label: "Province",
          widget: "text",
          maxLength: 25,
          hideOnTable: true,
          tooltip:
            "Enter the province in which the person/recruiter is located, if applicable. If the geographic location does not have a province designation, enter “N/A.”",
        },
      ],
    },
  ],
};
